const PRODUCT_CARD_IMAGE_SWAP_SELECTOR = '[data-product-card-image-swap="true"]';
const MOBILE_MEDIA_QUERY = "(max-width: 989px)";
const AUTOPLAY_DELAY_MS = 5000;

type ImageSwapMode = "desktop_hover" | "mobile_auto_timer";

class ProductCardImageSwapController {
  private card: HTMLElement;
  private alternateImage: HTMLImageElement | null;
  private mediaQuery: MediaQueryList;
  private reducedMotionQuery: MediaQueryList;
  private intervalId: number | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private isInViewport = false;

  constructor(card: HTMLElement) {
    this.card = card;
    this.alternateImage = card.querySelector("[data-image-swap-alternate]");
    this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
  }

  connect(): void {
    if (!this.alternateImage) return;

    this.card.addEventListener("mouseenter", this.handleMouseEnter);
    this.card.addEventListener("mouseleave", this.handleMouseLeave);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.mediaQuery.addEventListener("change", this.handleBreakpointChange);
    this.reducedMotionQuery.addEventListener("change", this.handleReducedMotionChange);

    this.updateMode();
  }

  private setupViewportObserver(): void {
    if (!this.mediaQuery.matches || !("IntersectionObserver" in window)) {
      this.isInViewport = true;
      this.startMobileTimer();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
      threshold: 0.1,
    });
    this.intersectionObserver.observe(this.card);
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    const entry = entries[0];
    if (!entry) return;

    this.isInViewport = entry.isIntersecting;
    if (this.isInViewport) {
      this.startMobileTimer();
    } else {
      this.stopMobileTimer();
      this.showPrimary();
    }
  }

  private updateMode(): void {
    this.stopMobileTimer();
    this.showPrimary();

    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
    this.isInViewport = false;

    if (this.mediaQuery.matches) {
      this.setupViewportObserver();
      return;
    }

    this.isInViewport = true;
    this.startMobileTimer();
  }

  private handleMouseEnter(): void {
    if (this.mediaQuery.matches) return;
    this.showAlternate("desktop_hover");
  }

  private handleMouseLeave(): void {
    if (this.mediaQuery.matches) return;
    this.showPrimary();
  }

  private startMobileTimer(): void {
    this.stopMobileTimer();

    if (
      !this.mediaQuery.matches ||
      !this.isInViewport ||
      document.hidden ||
      this.reducedMotionQuery.matches
    ) {
      return;
    }

    this.intervalId = window.setInterval(() => {
      if (!this.isInViewport || document.hidden) return;

      const isAlternateVisible = this.card.classList.contains("is-alternate-visible");
      this.toggleImage(!isAlternateVisible, "mobile_auto_timer");
    }, AUTOPLAY_DELAY_MS);
  }

  private stopMobileTimer(): void {
    if (this.intervalId === null) return;

    window.clearInterval(this.intervalId);
    this.intervalId = null;
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.stopMobileTimer();
      return;
    }

    this.startMobileTimer();
  }

  private handleBreakpointChange(): void {
    this.updateMode();
  }

  private handleReducedMotionChange(): void {
    this.updateMode();
  }

  disconnect(): void {
    this.stopMobileTimer();
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;

    this.card.removeEventListener("mouseenter", this.handleMouseEnter);
    this.card.removeEventListener("mouseleave", this.handleMouseLeave);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.mediaQuery.removeEventListener("change", this.handleBreakpointChange);
    this.reducedMotionQuery.removeEventListener("change", this.handleReducedMotionChange);
  }

  private showAlternate(mode: ImageSwapMode): void {
    this.toggleImage(true, mode);
  }

  private showPrimary(): void {
    this.toggleImage(false);
  }

  private toggleImage(showAlternate: boolean, mode?: ImageSwapMode): void {
    this.card.classList.toggle("is-alternate-visible", showAlternate);

    if (showAlternate && mode) {
      this.trackAlternateVisibility(mode);
    }
  }

  private trackAlternateVisibility(interactionMode: ImageSwapMode): void {
    if (this.card.dataset.imageSwapViewed === "true") return;

    this.card.dataset.imageSwapViewed = "true";
    window.__tracking?.trackEvent("view_product_card_alternate_image", {
      product_id: this.card.dataset.productId || "",
      product_handle: this.card.dataset.productHandle || "",
      collection_handle: this.card.dataset.imageSwapCollection || "",
      interaction_mode: interactionMode,
      primary_image_type: this.card.dataset.imageSwapPrimaryType || "still",
      alternate_image_type: this.card.dataset.imageSwapAlternateType || "model",
      experiment_variant: this.card.dataset.imageSwapVariant || "still_model",
    });
  }
}

const controllers = new WeakMap<HTMLElement, ProductCardImageSwapController>();

function initializeProductCardImageSwap(root: ParentNode = document): void {
  const cards = Array.from(root.querySelectorAll<HTMLElement>(PRODUCT_CARD_IMAGE_SWAP_SELECTOR));
  if (root instanceof HTMLElement && root.matches(PRODUCT_CARD_IMAGE_SWAP_SELECTOR)) {
    cards.unshift(root);
  }

  cards.forEach((card) => {
    if (controllers.has(card)) return;

    const controller = new ProductCardImageSwapController(card);
    controllers.set(card, controller);
    controller.connect();
  });
}

function getProductCardImageSwapCards(node: Node): HTMLElement[] {
  if (!(node instanceof HTMLElement)) return [];

  const cards = Array.from(node.querySelectorAll<HTMLElement>(PRODUCT_CARD_IMAGE_SWAP_SELECTOR));
  if (node.matches(PRODUCT_CARD_IMAGE_SWAP_SELECTOR)) cards.unshift(node);

  return cards;
}

function disconnectProductCardImageSwap(root: Node): void {
  getProductCardImageSwapCards(root).forEach((card) => {
    controllers.get(card)?.disconnect();
    controllers.delete(card);
  });
}

function observeNewCards(): void {
  if (!document.body || !("MutationObserver" in window)) return;

  const observer = new MutationObserver((records) => {
    const addedNodes = new Set<Node>();
    const removedNodes = new Set<Node>();

    records.forEach((record) => {
      record.addedNodes.forEach((node) => addedNodes.add(node));
      record.removedNodes.forEach((node) => removedNodes.add(node));
    });

    removedNodes.forEach((node) => disconnectProductCardImageSwap(node));
    addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) initializeProductCardImageSwap(node);
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function initialize(): void {
  initializeProductCardImageSwap();
  observeNewCards();
}

const globalScope = window as typeof window & {
  __insiderProductCardImageSwapInitialized?: boolean;
};

if (!globalScope.__insiderProductCardImageSwapInitialized) {
  globalScope.__insiderProductCardImageSwapInitialized = true;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", () => initializeProductCardImageSwap());
}
